import { StepperSelectionEvent, STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';
import { EventService } from 'src/app/services/event.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { TrimTextTransformPipe } from 'src/app/shared/pipes/trim-text-transform.pipe';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { EventStepperDataService } from './event-stepper-data.service';
import { EventSummaryComponent } from './event-summary.component';

type EventStepState = {
  detail: boolean,
  locations: boolean,
  people: boolean,
  attachments: boolean,
  summary: boolean
};

@Component({
  selector: 'ness-event-stepper-container',
  templateUrl: './event-stepper-container.component.html',
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: {
      displayDefaultIndicatorType: false
    }
  },
    TrimTextTransformPipe]
})
export class EventStepperContainerComponent implements OnInit, OnDestroy {
  private eventData: ServerModels.ENESS_EventDTO;
  private stagingId: string = null;
  private _eventStateData: any = {};
  private _destroyed$: Subject<void> = new Subject<void>();
  private _returnUrl: string = null;
  @ViewChild('stepper') private myStepper: MatStepper;


  @ViewChild('stepper', { static: false }) stepperCmp: MatStepper;
  @ViewChild('summary', { static: false }) summaryCmp: EventSummaryComponent;
  eventLEPartners: ServerModels.ENESS_XREF_EventsLEPartnersDTO[] = [];
  eventLocations: ServerModels.ENESS_XREF_EventsLocationsDTO[] = [];
  eventPeople: ServerModels.ENESS_XREF_EventsPeopleDTO[] = [];
  eventAttachments: ServerModels.ENESS_XREF_EventAttachmentsDTO[] = [];
  eventFormGroup: FormGroup = this.fb.group({
    EventStagingID: '',
    EventID: '',
    AgencyID: '',
    EventDateOffset: new FormControl<Date>(null, [Validators.required]),
    TimeZoneName: '',
    TimeZoneID: ['', Validators.required],
    Summary: ['', Validators.maxLength(1000)],
    Narrative: ['', Validators.maxLength(2147483645)],
    Notes: ['', Validators.maxLength(2147483645)],
    StatusID: ['', Validators.required],
    OtherStatus: ['', Validators.maxLength(80)],
    CrimeTypeID: ['', Validators.required],
    OtherCrimeType: ['', Validators.maxLength(80)],
    LECaseNumberID: '',
    LECaseNumberStagingID: '',
    ENESS_XREF_LECaseNumbers: this.fb.group({
      LECaseNumberStagingID: '',
      LECaseNumberID: '',
      LECaseNumber: ['', [NESSValidators.required, Validators.maxLength(80)]],
      LEAlternateCaseNumber: ['', Validators.maxLength(80)],
      NIBINAmendedLECaseNumber: ''
    }),
    ENESS_REF_Agency: this.fb.group({
      AgencyID: [{ value: '', disabled: true }, Validators.required],
      AgencyName: '',
      CountyID: '',
      ENESS_REF_Counties: this.fb.group({
        CountyID: '',
        StateID: ''
      })
    }, { validator: NESSValidators.requireDisabled }),
    ENESS_XREF_EventsLEPartners: [...this.eventLEPartners],
    ENESS_XREF_EventsLocations: [...this.eventLocations],
    ENESS_XREF_EventsPeople: [...this.eventPeople],
    ENESS_XREF_EventAttachments: [...this.eventAttachments]
  });
  submitted: boolean = false;
  perservedAgencyId: string = null;

  private _stepState = {
    detail: true,
    locations: false,
    people: false,
    attachments: false,
    summary: false
  } as EventStepState;
  stepStateSubj: BehaviorSubject<EventStepState> = new BehaviorSubject(this._stepState);
  stepState$: Observable<EventStepState> = this.stepStateSubj.asObservable();


  // Bits for navigation state
  readonly DETAIL_STEP = 1;
  readonly LOCATIONS_STEP = 1 << 1;
  readonly PEOPLE_STEP = 1 << 2;
  readonly ATTACHMENTS_STEP = 1 << 3;
  readonly SUMMARY_STEP = 1 << 4;
  readonly ALL_STEPS = this.DETAIL_STEP | this.LOCATIONS_STEP | this.PEOPLE_STEP | this.ATTACHMENTS_STEP | this.SUMMARY_STEP;

  constructor(public eventDataSvc: EventStepperDataService,
    private messageService: MessageService,
    private eventService: EventService,
    private previousRouteService: PreviousRouteService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router) {

    this._eventStateData = this.router.getCurrentNavigation()?.extras?.state;
    if (!!this._eventStateData) {
      if (!!this._eventStateData['event']) {
        this.eventData = JSON.parse(this._eventStateData['event']) as ServerModels.ENESS_EventDTO;
        let eventDateString = '' + (this.eventData.EventDateOffset || '');
        //remove the offset
        let eventDateStringWithoutOffset = eventDateString.substring(0, eventDateString.lastIndexOf('.'));
        //create date in local time
        this.eventData.EventDateOffset = new Date(eventDateStringWithoutOffset);
      }
      if (!!this._eventStateData['mode']) {
        this.eventDataSvc.mode = this._eventStateData['mode'];
      }
    } else {
      this.eventDataSvc.mode = null;
    }
  }

  ngOnInit() {
    this._returnUrl = (this.previousRouteService.getReturnUrl() || '/events');
    this.route.paramMap.subscribe((params: ParamMap) => {
      this.stagingId = params.get('sid');
      this.eventDataSvc.eventId = params.get('id') === 'null' ? '' : params.get('id');
      this.eventDataSvc.importEventId = params.get('ipid') === 'null' ? '' : params.get('ipid');
      this.eventDataSvc.mode = !!this.stagingId && (!!this.eventDataSvc.eventId || !!this.eventDataSvc.importEventId) ? 'Edit' : 'Add';

      this.eventFormGroup.statusChanges.pipe(takeUntil(this._destroyed$)).subscribe(
        (val: any) => this.setStepState()
      );

      this.eventDataSvc.stepStatus$.pipe(takeUntil(this._destroyed$)).subscribe(
        (status: { step: string, state: boolean }) => this.setStepState(status)
      );

      // Edit imported event
      if (!!this.stagingId && !!this.eventDataSvc.importEventId) {
        this.eventFormGroup.get('EventStagingID').setValue(this.stagingId);
        this.eventDataSvc.mode = 'Edit';
        this.loadImportEvent();
      } else if (!!this.stagingId) {
        this.eventFormGroup.get('EventStagingID').setValue(this.stagingId);
        this.eventDataSvc.getEventByStagingId(this.stagingId).pipe(takeUntil(this._destroyed$)).subscribe((stagingEvent: ServerModels.ENESS_EventDTO) => {
          if (!!stagingEvent && Object.keys(stagingEvent).length) {
            this._setEvent(stagingEvent);
          } else {
            if (!!this.eventDataSvc.eventId) {
              this.eventFormGroup.get('EventID').setValue(this.eventDataSvc.eventId);
            }

            if (!!this.eventDataSvc.mode) {
              if (this.eventDataSvc.mode.toLowerCase() == 'add') {
                this.eventData = this.eventFormGroup.value as ServerModels.ENESS_EventDTO;
                this._setFormDataValues();
              }

              if (this.eventDataSvc.mode.toLowerCase() == 'edit') {
                this.loadEventData();
              }
            } else if (!!this.stagingId && !!this.eventDataSvc.eventId) {
              this.eventDataSvc.mode = 'Edit';
              this.loadEventData();
            }
          }

          this.eventDataSvc.crimeTypesList$.pipe(takeUntil(this._destroyed$)).subscribe((crimeTypes: ServerModels.ENESS_REF_CrimeTypes[]) => {
            if (!!this.eventData) {
              crimeTypes.forEach(crime => {
                if (!this.eventData.CrimeTypeID && !!this.eventData.OtherCrimeType && crime.CrimeTypeName.toLowerCase() === this.eventData.OtherCrimeType.toLowerCase()) {
                  this.eventFormGroup.get('CrimeTypeID').setValue(crime.CrimeTypeID);
                  this.eventFormGroup.get('OtherCrimeType').setValue(this.eventData.OtherCrimeType?.substring(0, 80));
                  return;
                }
              });
            }
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.eventDataSvc.mode = null;
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public setStepState(stepState?: { step: string, state: boolean }): void {
    let bitMask: number = 0;

    if (stepState) {
      if (stepState.step === 'details') {
        bitMask = stepState.state ? bitMask | this.ALL_STEPS : bitMask | this.DETAIL_STEP;
      }
      if (stepState.step === 'people') {
        bitMask = stepState.state ? bitMask | this.ALL_STEPS : bitMask | this.PEOPLE_STEP;
      }
      if (stepState.step === 'summary') {
        bitMask = bitMask | this.ALL_STEPS;
      }
    } else if (this.eventFormGroup.valid) {
      bitMask = bitMask | this.ALL_STEPS;
    }

    let newStepState = {
      detail: (bitMask & this.DETAIL_STEP) == this.DETAIL_STEP,
      locations: (bitMask & this.LOCATIONS_STEP) == this.LOCATIONS_STEP,
      people: (bitMask & this.PEOPLE_STEP) == this.PEOPLE_STEP,
      attachments: (bitMask & this.ATTACHMENTS_STEP) == this.ATTACHMENTS_STEP,
      summary: (bitMask & this.SUMMARY_STEP) == this.SUMMARY_STEP
    };
    this.stepStateSubj.next(newStepState);
  }

  public loadEventData(): void {
    this.eventService.getEditEventDTOById(this.stagingId, this.eventDataSvc.eventId).pipe(take(1)).subscribe(
      (event: ServerModels.ENESS_EventDTO) => this._setEvent(event)
    );
  }

  public loadImportEvent() {
    this.eventService.editUploadedEventById(this.stagingId, this.eventDataSvc.importEventId).pipe(take(1)).subscribe(
      (event: ServerModels.ENESS_EventDTO) => this._setEvent(event)
    );
  }

  private _setEvent(eventDTO: ServerModels.ENESS_EventDTO): void {
    if (!Object.keys(eventDTO).length) {
      this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: 'Error:', detail: 'Returned event is empty.' });
      return;
    }
    this.eventDataSvc.crimeTypesList$ = this.eventDataSvc.getCrimeTypes(eventDTO.CrimeTypeID);
    if (!eventDTO.ENESS_XREF_LECaseNumbers || !Object.keys(eventDTO.ENESS_XREF_LECaseNumbers).length) {
      eventDTO.ENESS_XREF_LECaseNumbers = {
        LECaseNumberStagingID: null,
        LECaseNumberID: null,
        LECaseNumber: null,
        LEAlternateCaseNumber: null,
        NIBINAmendedLECaseNumber: null,
        CreatedOn: null
      };
    }
    if (!eventDTO.ENESS_REF_Agency || !Object.keys(eventDTO.ENESS_REF_Agency).length) {
      eventDTO.ENESS_REF_Agency = {
        AgencyID: '',
        AgencyName: null,
        CountyID: '',
        CreatedOn: null,
        ENESS_REF_Counties: {
          StateID: '',
          CountyID: '',
          CreatedOn: null
        }
      };
    }
    this.eventDataSvc.setEventLECaseNumberValue(eventDTO.ENESS_XREF_LECaseNumbers.LECaseNumber);
    this.eventData = eventDTO;
    this._setFormDataValues();
  }

  private _setFormDataValues(): void {
    if (!!this.eventData.EventDateOffset) {
      //cast event date offset to string
      let eventDateString = '' + (this.eventData.EventDateOffset || '');
      //remove the offset
      let eventDateStringWithoutOffset = eventDateString.substring(0, eventDateString.length - 6);
      //create date in local time
      this.eventData.EventDateOffset = new Date(eventDateStringWithoutOffset);
    }

    this.eventLEPartners = this.eventData.ENESS_XREF_EventsLEPartners || [];
    this.eventLocations = this.eventData.ENESS_XREF_EventsLocations || [];
    this.eventPeople = this.eventData.ENESS_XREF_EventsPeople || [];
    this.eventAttachments = this.eventData.ENESS_XREF_EventAttachments || [];
    this.eventFormGroup.patchValue({
      EventStagingID: this.eventData.EventStagingID || this.stagingId,
      EventID: this.eventData.EventID || this.eventDataSvc.eventId,
      LECaseNumberID: this.eventData.LECaseNumberID,
      LECaseNumberStagingID: this.eventData.LECaseNumberStagingID,
      AgencyID: this.eventData.AgencyID || '',
      EventDateOffset: this.eventData.EventDateOffset || null,
      TimeZoneID: this.eventData.TimeZoneID || '',
      TimeZoneName: this.eventData.TimeZoneName,
      Summary: this.eventData.Summary?.substring(0, 1000),
      Narrative: this.eventData.Narrative,
      Notes: this.eventData.Notes,
      StatusID: this.eventData.StatusID || '',
      OtherStatus: this.eventData.OtherStatus,
      CrimeTypeID: this.eventData.CrimeTypeID || '',
      OtherCrimeType: this.eventData.OtherCrimeType?.substring(0, 80),
      ENESS_XREF_LECaseNumbers: {
        LECaseNumberStagingID: this.eventData.ENESS_XREF_LECaseNumbers.LECaseNumberStagingID,
        LECaseNumberID: this.eventData.ENESS_XREF_LECaseNumbers.LECaseNumberID || '',
        LECaseNumber: this.eventData.ENESS_XREF_LECaseNumbers.LECaseNumber,
        LEAlternateCaseNumber: this.eventData.ENESS_XREF_LECaseNumbers.LEAlternateCaseNumber,
        NIBINAmendedLECaseNumber: this.eventData.ENESS_XREF_LECaseNumbers.NIBINAmendedLECaseNumber
      },
      ENESS_REF_Agency: {
        AgencyID: this.eventData.ENESS_REF_Agency.AgencyID || '',
        AgencyName: this.eventData.ENESS_REF_Agency.AgencyName,
        CountyID: this.eventData.ENESS_REF_Agency.CountyID,
        ENESS_REF_Counties: {
          CountyID: this.eventData.ENESS_REF_Agency.ENESS_REF_Counties.CountyID,
          StateID: this.eventData.ENESS_REF_Agency.ENESS_REF_Counties.StateID
        }
      },
      ENESS_XREF_EventsLEPartners: [...this.eventLEPartners],
      ENESS_XREF_EventsLocations: [...this.eventLocations],
      ENESS_XREF_EventsPeople: [...this.eventPeople],
      ENESS_XREF_EventAttachments: [...this.eventAttachments]
    });
  }

  public onSubmit(): void {
    this.submitted = this.eventFormGroup.valid;
    if (this.eventFormGroup.valid) {
      this.eventData = this.eventFormGroup.value as ServerModels.ENESS_EventDTO;
      this._addOrUpdateEvent();
    }
  }

  private _addOrUpdateEvent(): void {
    //compensate for client's browser time offset
    //let browserTimeOffsetMinutes = this.eventData.EventDateOffset.getTimezoneOffset();
    //this.eventData.EventDateOffset = new Date(this.eventData.EventDateOffset.getTime() - (browserTimeOffsetMinutes * 60000));
    let markImportItem = this.eventService.markImportItemIsFixedAsync(this.eventDataSvc.importEventId);
    this.messageService.clear();
    let eventIds = {
      'EventID': this.eventDataSvc.eventId,
      'EventStagingID': this.eventData.EventStagingID
    }

    this.eventDataSvc.createOrUpdateEvent(eventIds).pipe(take(1)).subscribe({
      next: (message: string) => {
        // Do something if error
        let result: string = message;
        if (result != 'Success') {
          if (result.indexOf('Duplicate record') > -1) {
            this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: 'Unable to create event.', detail: 'An event already exists for law enforcement case number ' + this.eventData.ENESS_XREF_LECaseNumbers.LECaseNumber });
          }
          else {
            this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: 'This event could not be saved. Please try again.', detail: '' });
            this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: '', detail: 'If this issue persists, please contact NESS Support.' });
          }
          this.submitted = false;
        }
        else {
          this.submitted = true;
          if (this.eventDataSvc.importEventId) {
            markImportItem.pipe(take(1)).subscribe({ next: (data: any) => { } })
          }
          if (!!this._returnUrl) {
            this.router.navigateByUrl(this._returnUrl).then(() => {
              this.previousRouteService.returnUrl = null;
            });
          } else {
            this.router.navigate(['/events']);
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: ' This event could not be saved. Please try again.', detail: err.error });
        this.submitted = false;
      }
    });
  }

  public selectTargetStep(stepName: string): void {
    let _targetStep = this.stepperCmp.steps.find(step => step.state == stepName);
    if (_targetStep)
      _targetStep.select();
  }

  public onStepChange(stepSelectionEvent: StepperSelectionEvent): void {
    let updateState: Function = (stepEvt: StepperSelectionEvent) => {
      switch (stepEvt.selectedStep.state) {
        case 'details':
          this.eventDataSvc.stepStatusChange({ step: 'details', state: !this.eventFormGroup.invalid && !this.isMissingPersonType() });
          // Workaround for Agency dropdown being unset on step change
          this.eventFormGroup.get('ENESS_REF_Agency.AgencyID').setValue(this.perservedAgencyId, { onlySelf: true, emitEvent: false });
          break;
        case 'people':
          this.eventDataSvc.stepStatusChange({ step: 'people', state: !this.isMissingPersonType() });
          break;
        case 'summary':
          if (this.eventFormGroup.invalid || this.isMissingPersonType()) {
            this.eventDataSvc.stepStatusChange({ step: 'summary', state: true });
          }
          this.summaryCmp.resetNotifications();
          this.summaryCmp.setNotifications();
          break;
      }
    }

    if (stepSelectionEvent.previouslySelectedStep.state == 'details') {
      let formData = <ServerModels.ENESS_EventDTO>this.eventFormGroup.getRawValue();
      this.perservedAgencyId = formData.ENESS_REF_Agency.AgencyID;
      formData.CreatedBy = this.eventData.CreatedBy;
      formData.CreatedOn = this.eventData.CreatedOn;
      if (!!formData.EventDateOffset) {
        //compensate for client's browser time offset
        let browserTimeOffsetMinutes = formData.EventDateOffset.getTimezoneOffset();
        formData.EventDateOffset = new Date(formData.EventDateOffset.getTime() - browserTimeOffsetMinutes * 60 * 1000);
      }

      this.eventDataSvc.addOrUpdateEventDetailsStaging(this.eventDataSvc.setEventDetails(formData)).pipe(take(1)).subscribe({
        next: (result: ServerModels.ENESS_EventDTO) => {
          if (!!result && !!Object.keys(result).length) {
            this.eventData = { ...this.eventFormGroup.value, ...this.eventDataSvc.setEventDetails(result) };
            if (!!result.EventDateOffset) {
              //cast event date offset to string
              let eventDateString = '' + (this.eventData.EventDateOffset || '');
              //remove the offset
              let eventDateStringWithoutOffset = eventDateString.substring(0, eventDateString.length - 6);
              //create date in local time
              this.eventData.EventDateOffset = new Date(eventDateStringWithoutOffset);
            }

            this.eventFormGroup.patchValue(this.eventData);
            updateState(stepSelectionEvent);
          }
        },
        error: (err: any) => {
          this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: 'Error:', detail: err.error })
          this.myStepper.previous();
        }
      });
    } else {
      updateState(stepSelectionEvent);
    }
  }

  public isMissingPersonType(): boolean {
    return this.eventFormGroup.get('ENESS_XREF_EventsPeople').value.some((p: ServerModels.ENESS_XREF_EventsPeople) => p.ENESS_XREF_EventsPeopleTypes && p.ENESS_XREF_EventsPeopleTypes.length === 0);
  }
}
